import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AuthenticationService } from '../_services';

/**
 * Tracks content changes by tagging outgoing requests with audit headers.
 *
 * Only requests where the user CREATES, UPDATES or DELETES data are tracked
 * (verified against a full scan of the codebase's API calls). Pure reads
 * (list/show/get/validate), auth/session actions and temp-asset cleanup are
 * left untouched.
 *
 * NOTE: this app uses POST for reads too (server-side datatables hit `.../get`)
 * and performs some DELETEs over HTTP GET (e.g. `snippets/delete/:id`,
 * `pages/delete-how-to/:id`), so tracking cannot rely on the HTTP method alone.
 *
 * On a tracked request it sets five headers (all URL-encoded — HTTP headers are
 * ISO-8859-1 only, so the backend should decodeURIComponent() each value):
 *   X-User-Id, X-User-Name, X-Action-Performed, X-Page-Type, X-Page-Slug
 */
@Injectable()
export class ChangeMessageInterceptor implements HttpInterceptor {

    // Friendly singular name for each API resource group (used in the action text).
    private readonly resourceNames: { [key: string]: string } = {
        'pages': 'page',
        'blogs': 'blog',
        'blogcategories': 'blog category',
        'writers': 'writer',
        'authors': 'author',
        'new-service-pages': 'service page',
        'pdfs': 'PDF',
        'snippets': 'snippet',
        'user': 'user account',
        'users': 'user account',
        'blogger': 'blogger',
        'lead': 'lead',
        'sample_with_reports': 'sample with reports',
        'sample-with-reports': 'sample with reports',
        'locations': 'location',
        'project-stats': 'project stats',
        'admin': 'admin',
    };

    // Title-cased "Page Type" label for each resource group.
    private readonly pageTypeByGroup: { [key: string]: string } = {
        'pages': 'Page',
        'blogs': 'Blog',
        'blogcategories': 'Blog Category',
        'writers': 'Writers',
        'authors': 'Authors',
        'user': 'Users',
        'users': 'Users',
        'blogger': 'Blogger',
        'lead': 'Lead',
        'new-service-pages': 'Service Page',
        'pdfs': 'PDF',
        'snippets': 'Snippet',
        'sample_with_reports': 'Sample',
        'sample-with-reports': 'Sample',
        'locations': 'Location',
        'project-stats': 'Project Stats',
        'admin': 'Admin',
    };

    // A page_type value in the request body overrides the group-based label.
    private readonly pageTypeByBody: { [key: string]: string } = {
        'blog': 'Blog',
        'guide': 'Guide',
        'article': 'Article',
        'service': 'Service Page',
        'new-service-page': 'Service Page',
    };

    // Exact, hand-written action text for endpoints that don't fit the generic
    // resource/action pattern. First match wins.
    private readonly overrides: Array<{ pattern: RegExp; message: string }> = [
        { pattern: /blogs\/change-status/i,           message: 'Changed the blog publish status' },
        { pattern: /blogs\/update-verified-status/i,  message: 'Updated the blog verification status' },
        { pattern: /pages\/change-status/i,           message: 'Changed the page publish status' },
        { pattern: /pages\/schedule-publishing/i,     message: 'Scheduled the page for publishing' },
        { pattern: /pages\/blog-posting/i,            message: 'Updated the BlogPosting schema' },
        { pattern: /pages\/delete-blog-posting/i,     message: 'Removed the BlogPosting schema' },
        { pattern: /pages\/custom-schemas/i,          message: 'Updated a custom schema' },
        { pattern: /pages\/delete-custom-schemas/i,   message: 'Deleted a custom schema' },
        { pattern: /pages\/add-how-to-step/i,         message: 'Saved a How-To step' },
        { pattern: /pages\/delete-how-to-step/i,      message: 'Deleted a How-To step' },
        { pattern: /pages\/delete-how-to/i,           message: 'Removed the How-To section' },
        { pattern: /pages\/how-to/i,                  message: 'Updated the How-To section' },
        { pattern: /new-service-pages\/delete-section/i, message: 'Deleted a service page section' },
        { pattern: /new-service-pages\/update-pdf/i,     message: 'Updated the service page PDF' },
        { pattern: /new-service-pages\/banner-list/i,    message: 'Updated the service page banner' },
        { pattern: /sample_with_reports\/add-update/i,   message: 'Saved a sample with reports' },
        { pattern: /(^|\/)admin\/agent/i,             message: 'Saved an agent' },
    ];

    constructor(private authenticationService: AuthenticationService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.shouldTrack(request)) {
            return next.handle(request);
        }

        const user = this.userContext();
        const context = this.pageContext(request);

        const tagged = request.clone({
            setHeaders: {
                'X-User-Id':          this.enc(user.id),
                'X-User-Name':        this.enc(user.name),
                'X-Action-Performed': this.enc(this.describe(request)),
                'X-Page-Type':        this.enc(context.pageType),
                'X-Page-Slug':        this.enc(context.pageSlug),
            }
        });
        return next.handle(tagged);
    }

    // ---- what to track ---------------------------------------------------

    private shouldTrack(request: HttpRequest<any>): boolean {
        const method = request.method.toUpperCase();
        const path = (request.url.split('?')[0] || '').toLowerCase();

        // never track reads, auth/session actions, or temp-asset cleanup
        if (this.isRead(path) || this.isAuth(path) || this.isTempAsset(path)) {
            return false;
        }

        // GET is a read by default; track it only when it performs a delete
        // (this app deletes some things over GET, e.g. snippets/delete/:id).
        if (method === 'GET') {
            return /delete|remove/.test(path);
        }

        // remaining POST/PUT/PATCH/DELETE are genuine content mutations
        return true;
    }

    private isRead(path: string): boolean {
        return /(^|\/)get/.test(path)              // get, get-all, get-parents, getCategories, ...
            || /(^|\/)show/.test(path)             // show, show/:id
            || /validate-slug/.test(path)          // slug availability checks
            || /(^|\/)site-(authors|writers)/.test(path); // public listing reads
    }

    private isAuth(path: string): boolean {
        return /(^|\/)auth\//.test(path)
            || /(^|\/)(login|logout|signup)(\/|$)/.test(path)
            || /socailsignup/.test(path);
    }

    private isTempAsset(path: string): boolean {
        return /(^|\/)temp-(file|images?|pdf)/.test(path)
            || /(^|\/)upload-/.test(path);
    }

    // ---- header values ---------------------------------------------------

    private userContext(): { id: any, name: string } {
        const user = this.authenticationService.get_currentUserValue();
        const id = user
            ? (user.id !== undefined ? user.id
                : user._id !== undefined ? user._id
                : user.user_id)
            : undefined;
        return {
            id: id !== undefined ? id : 'guest',
            name: user ? (user.username || user.firstName || user.name || 'unknown') : 'guest',
        };
    }

    private pageContext(request: HttpRequest<any>): { pageType: string, pageSlug: string } {
        const group = this.resourceGroup(request.url);

        const bodyType = this.bodyValue(request, ['page_type', 'type', 'post_type']);
        const pageType = (bodyType && this.pageTypeByBody[bodyType.toLowerCase()])
            || this.pageTypeByGroup[group]
            || this.titleCase(bodyType || group)
            || 'Unknown';

        const slug = this.bodyValue(request, ['page_slug', 'slug', 'title_slug']);
        const id = this.recordId(request)
            || this.bodyValue(request, ['id', 'page_id', 'content_id', 'blog_id']);

        let pageSlug: string;
        if (slug && id) {
            pageSlug = `${slug} (ID: ${id})`;
        } else if (slug) {
            pageSlug = String(slug);
        } else if (id) {
            pageSlug = `ID: ${id}`;
        } else {
            pageSlug = 'N/A';
        }

        return { pageType, pageSlug };
    }

    // ---- action text -----------------------------------------------------

    private describe(request: HttpRequest<any>): string {
        const url = request.urlWithParams;

        for (const rule of this.overrides) {
            if (rule.pattern.test(url)) {
                return rule.message;
            }
        }

        const path = url.split('?')[0];
        const segments = this.pathSegments(url);
        const group = this.resourceGroup(url);
        const resource = this.resourceNames[group] || this.humanize(group) || 'record';

        const rest = segments.slice(segments.indexOf(group) + 1).filter(s => !/^\d+$/.test(s));
        const head = (rest[0] || '').toLowerCase();
        const method = request.method.toUpperCase();
        const hasId = /\/\d+(\/?$)/.test(path);

        const verbMatch = /^(add|update|delete|remove|create|save)(?:[-_](.+))?$/i.exec(head);
        let verb: string;
        let subject = '';

        if (verbMatch) {
            verb = verbMatch[1].toLowerCase();
            subject = verbMatch[2] ? this.humanize(verbMatch[2]) : '';
        } else {
            subject = head ? this.humanize(head) : '';
            verb = method === 'DELETE' ? 'delete'
                : (method === 'PUT' || method === 'PATCH') ? 'update'
                : (method === 'POST' && !subject && !hasId) ? 'create'
                : 'save';
        }

        if (subject && resource.indexOf(subject) !== -1) {
            subject = '';
        }

        switch (verb) {
            case 'add':
            case 'create':
                return subject
                    ? `Added ${this.article(subject)} ${subject} to the ${resource}`
                    : `Created a new ${resource}`;
            case 'delete':
            case 'remove':
                return subject ? `Deleted the ${subject} from the ${resource}` : `Deleted the ${resource}`;
            case 'update':
            case 'save':
            default:
                return subject ? `Updated the ${subject} on the ${resource}` : `Updated the ${resource}`;
        }
    }

    // ---- helpers ---------------------------------------------------------

    private pathSegments(url: string): string[] {
        return url.split('?')[0].split('/').filter(Boolean).map(s => s.toLowerCase());
    }

    private resourceGroup(url: string): string {
        const segments = this.pathSegments(url);
        const start = segments.findIndex(s => this.resourceNames[s] !== undefined);
        return start >= 0 ? segments[start] : (segments[segments.length - 2] || '');
    }

    // last numeric path segment, or an *id query param (e.g. ?page_id=12)
    private recordId(request: HttpRequest<any>): string | null {
        const path = request.url.split('?')[0];
        const ids = path.split('/').filter(s => /^\d+$/.test(s));
        if (ids.length) {
            return ids[ids.length - 1];
        }
        const query = request.urlWithParams.split('?')[1];
        if (query) {
            const match = /(?:^|&)[a-z_]*id=(\d+)/i.exec(query);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    // reads a value out of a JSON object body or a FormData body
    private bodyValue(request: HttpRequest<any>, keys: string[]): string | null {
        const body: any = request.body;
        if (!body) {
            return null;
        }

        if (typeof FormData !== 'undefined' && body instanceof FormData) {
            for (const key of keys) {
                if (body.has(key)) {
                    const value = body.get(key);
                    if (value !== null && value !== undefined && value !== '') {
                        return String(value);
                    }
                }
            }
            return null;
        }

        if (typeof body === 'object') {
            for (const key of keys) {
                if (body[key] !== null && body[key] !== undefined && body[key] !== '') {
                    return String(body[key]);
                }
            }
            // look one level deep in common wrapper objects
            for (const wrapper of ['page', 'pageData', 'blog', 'data', 'writer', 'author']) {
                const nested = body[wrapper];
                if (nested && typeof nested === 'object') {
                    for (const key of keys) {
                        if (nested[key] !== null && nested[key] !== undefined && nested[key] !== '') {
                            return String(nested[key]);
                        }
                    }
                }
            }
        }
        return null;
    }

    private article(word: string): string {
        return /^[aeiou]/i.test(word) ? 'an' : 'a';
    }

    private humanize(value: string): string {
        return (value || '').replace(/[-_]+/g, ' ').trim().toLowerCase();
    }

    private titleCase(value: string): string {
        return this.humanize(value).replace(/\b\w/g, c => c.toUpperCase());
    }

    private enc(value: any): string {
        return encodeURIComponent(String(value === undefined || value === null ? '' : value));
    }
}
